import React, { ReactElement, ReactNode } from 'react'
import {
  OmForm,
  OmFormItem,
  OmInput,
  OmInputNumber,
  OmCheckbox,
  OmSelect,
  OmRadioGroup,
  OmRadio,
  OmDatePicker,
  OmTimePicker,
  OmDescriptions,
  OmDescriptionsItem,
  OmButton,
  OmDialog,
  OmTable,
  OmList,
  OmListItem,
  OmPagination,
  OmIcon,
} from '@owma/kit'
import type { OmFormInstance, OmSelectOption, OmTableColumn } from '@owma/kit'
import moment from 'moment'

import JsonEditor from 'components/JsonEditor'
import SchemaJsonEditor from 'components/SchemaJsonEditor'
import JsonEditorChooser from 'components/JsonEditorChooser'
import DeviceSizeChecker from 'components/DeviceSizeChecker'

import { config } from '../def'
import type { NineGQLUIAdapter, ResultObjContract } from '../ninegql/uiAdapter'
import {
  timeFormat,
  timeFormatUTC,
  timeFormatUTCDefault,
  timeFormatDefault,
  removeTag,
} from 'utils'

const ngqlConfig = config || {}

function str(val: string | null | undefined): string {
  if (val == null || val.length === 0) {
    return '-'
  }
  return val
}

function extend<T extends Record<string, any>>(
  dest: T | null | undefined,
  obj: Record<string, any> | null | undefined,
): T {
  const target = (dest || {}) as T
  if (obj != null) {
    Object.entries(obj).forEach(([key, value]) => {
      ;(target as any)[key] = value
    })
  }

  return target
}

interface FieldInfo {
  name: string
  type: string
  desc?: string
  editLabel?: ReactNode
  label?: ReactNode
  options?: any
  origin?: any
  config?: any
  initValue?: any
  afterText?: string
  value?: any
  state?: any
}

interface ReactObj {
  forceUpdate?: () => void
  setState?: (state: any) => void
  state?: any
  _ngqlObj?: Record<string, any>
  [key: string]: any
}

interface AttrResult {
  attrs: any
  inputAttrs: any
}

function getAttrs(info: FieldInfo, reactObj: ReactObj | null, value: any, values: any): AttrResult {
  let attrs: any = {}
  let inputAttrs: any = {}

  if (info.options && info.options.attrs) {
    attrs = extend(attrs, info.options.attrs)
  }
  if (info.config) {
    attrs = extend(attrs, info.config)
  }
  if (info.origin) {
    if (info.origin.default != null) {
      attrs = extend(attrs, { initialValue: info.origin.default })
    }
    if (info.origin.mode === 'read' || info.origin.mode === 'readonly') {
      inputAttrs.disabled = true
    }
  }

  if (info.initValue != null) {
    attrs.initialValue = info.initValue
  }

  const cname = `nine-${info.name}`
  if (info.origin && info.origin.className != null) {
    let { className } = info.origin
    const cnames = className.split(' ')
    if (className && cnames.indexOf(cname) === -1) {
      className += ` ${cname}`
    }
    attrs.className = className
  } else {
    attrs.className = cname
  }

  if (info.origin.inputAttrs) {
    const originInputAttr =
      typeof info.origin.inputAttrs === 'function'
        ? info.origin.inputAttrs(info, reactObj, value, values)
        : info.origin.inputAttrs
    inputAttrs = extend(inputAttrs, originInputAttr)
  }

  if (attrs.rules != null) {
    // antd에서는 rules에 message 등 정해진 key이외의 값이 존재하면, 제대로 validation 체크가 되지 않으므로 messageKey를 삭제.
    const realRules: any[] = []
    attrs.rules.forEach((item: any) => {
      const realRule = {
        ...item,
      }
      delete realRule.messageKey
      realRules.push(realRule)
    })
    attrs.rules = realRules
  }

  return {
    attrs,
    inputAttrs,
  }
}

// export function createCSSLabel(columns) {
//   const vars = {}
//
//   columns.forEach((column, idx) => {
//     let title = null
//     if (column.mobileTitle != null) {
//       const tmp = removeTag(column.mobileTitle.trim())
//       title = tmp.length > 0 ? `'${tmp} : '` : ''
//     } else if (typeof column.title === 'string') {
//       const tmp = removeTag(column.title.trim())
//       title = tmp.length > 0 ? `'${tmp} : '` : ''
//     }
//     if (title != null) {
//       vars[`--label${idx + 1}`] = title
//     }
//   })
//
//   return vars
// }

function createInput(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)

  let children: ReactElement
  if (info.type === 'number') {
    if (attrs.initialValue != null && typeof attrs.initialValue === 'string') {
      attrs.initialValue = window.parseInt(attrs.initialValue)
    }
    children = <OmInputNumber placeholder={info.desc} {...inputAttrs} />
  } else if (info.type === 'checkbox') {
    children = <OmCheckbox {...inputAttrs} />
  } else if (info.type === 'url') {
    children = <OmInput type="url" placeholder={info.desc} {...inputAttrs} />
  } else if (
    info.type === 'datetime' ||
    info.type === 'datetime-utc' ||
    info.type === 'datetime-ufw' ||
    info.type === 'datetime-ufw-utc'
  ) {
    // 값 계약: 문자열 YYYY-MM-DDTHH:mm(native datetime-local). valueSetFilter/valueGetFilter 와 일치.
    children = <OmDatePicker showTime placeholder={info.desc} {...inputAttrs} />
  } else if (
    info.type === 'date' ||
    info.type === 'date-utc' ||
    info.type === 'date-ufw' ||
    info.type === 'date-ufw-utc'
  ) {
    // 값 계약: 문자열 YYYY-MM-DD.
    children = <OmDatePicker placeholder={info.desc} {...inputAttrs} />
  } else if (
    info.type === 'time' ||
    info.type === 'time-utc' ||
    info.type === 'time-ufw' ||
    info.type === 'time-ufw-utc'
  ) {
    // 값 계약: 문자열 HH:mm.
    children = <OmTimePicker placeholder={info.desc} {...inputAttrs} />
  } else {
    children = <OmInput placeholder={info.desc} {...inputAttrs} />
  }

  // const afterText = info.afterText ? info.afterText : ''

  return (
    <OmFormItem label={info.editLabel} name={info.name} key={info.name} {...attrs}>
      {children}
    </OmFormItem>
  )
}

function createTextarea(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)

  return (
    <OmFormItem label={info.editLabel} name={info.name} key={info.name} {...attrs}>
      <OmInput multiline placeholder={info.desc} {...inputAttrs} />
    </OmFormItem>
  )
}

function createJSONEditor(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)

  let lastError: { hasError: boolean } = {
    hasError: false,
  }

  const options = {
    mode: 'code' as const,
    // indent: 2,
    search: false,
  }

  const newRules: any[] = []
  let validator: any = null
  let validateRule: any = null

  if (attrs.rules != null) {
    attrs.rules.forEach((item: any) => {
      if (typeof item === 'function') {
        validator = item
      } else if (item != null && typeof item.validator === 'function') {
        validator = item
      } else if (item.validate != null) {
        validateRule = item
      } else {
        newRules.push(item)
      }
    })
  }

  if (validateRule == null && validator != null) {
    newRules.push(validator)
  } else {
    const validatorFunc =
      validator != null && typeof validator.validator === 'function'
        ? validator.validator
        : validator
    const message = validateRule != null ? validateRule.message : 'JSON Syntex error.'

    const newValidator = {
      validator: async (/* _, names */): Promise<void> => {
        if (lastError.hasError !== true) {
          if (validatorFunc != null) {
            validatorFunc()
          }
          return Promise.resolve()
        }
        return Promise.reject(new Error(message))
      },
    }

    newRules.push(newValidator)
  }

  attrs.rules = newRules

  const onChange = (e: any) => {
    console.log('onChange ~!!', e)
  }

  const onError = (e: { hasError: boolean }) => {
    console.log('onError ~!!', e)
    lastError = e
  }

  // attrs.getValueProps = (param1, param2) => {
  //   // eslint-disable-next-line prefer-rest-params
  //   console.log( ' getValueProp - ', arguments, param1, param2);
  // }

  return (
    <OmFormItem
      label={info.editLabel}
      name={info.name}
      key={info.name}
      valuePropName="value"
      {...attrs}
    >
      <JsonEditor
        style={inputAttrs.style}
        onError={onError}
        onChange={onChange}
        options={options}
        {...inputAttrs}
      />
    </OmFormItem>
  )
}

function createSchemaJSONEditor(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)

  let lastError: { hasError: boolean } = {
    hasError: false,
  }

  const newRules: any[] = []
  let validator: any = null
  let validateRule: any = null

  if (attrs.rules != null) {
    attrs.rules.forEach((item: any) => {
      if (typeof item === 'function') {
        validator = item
      } else if (item != null && typeof item.validator === 'function') {
        validator = item
      } else if (item.validate != null) {
        validateRule = item
      } else {
        newRules.push(item)
      }
    })
  }

  if (validateRule == null && validator != null) {
    newRules.push(validator)
  } else {
    const validatorFunc =
      validator != null && typeof validator.validator === 'function'
        ? validator.validator
        : validator
    const message = validateRule != null ? validateRule.message : 'JSON Syntex error.'

    const newValidator = {
      validator: async (/* _, names */): Promise<void> => {
        if (lastError.hasError !== true) {
          if (validatorFunc != null) {
            validatorFunc()
          }
          return Promise.resolve()
        }
        return Promise.reject(new Error(message))
      },
    }

    newRules.push(newValidator)
  }

  attrs.rules = newRules

  const onChange = (e: any) => {
    console.log('onChange ~!!', e)
  }

  const onError = (e: { hasError: boolean }) => {
    console.log('onError ~!!', e)
    lastError = e
  }

  return (
    <OmFormItem
      label={info.editLabel}
      name={info.name}
      key={info.name}
      valuePropName="value"
      {...attrs}
    >
      <SchemaJsonEditor
        style={inputAttrs.style}
        onError={onError}
        onChange={onChange}
        {...inputAttrs}
      />
    </OmFormItem>
  )
}

function createJSONEditorChooser(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)

  let lastError: { hasError: boolean } = {
    hasError: false,
  }

  const options = {
    mode: 'code' as const,
    // indent: 2,
    search: false,
  }

  const newRules: any[] = []
  let validator: any = null
  let validateRule: any = null

  if (attrs.rules != null) {
    attrs.rules.forEach((item: any) => {
      if (typeof item === 'function') {
        validator = item
      } else if (item != null && typeof item.validator === 'function') {
        validator = item
      } else if (item.validate != null) {
        validateRule = item
      } else {
        newRules.push(item)
      }
    })
  }

  if (validateRule == null && validator != null) {
    newRules.push(validator)
  } else {
    const validatorFunc =
      validator != null && typeof validator.validator === 'function'
        ? validator.validator
        : validator
    const message = validateRule != null ? validateRule.message : 'JSON Syntex error.'

    const newValidator = {
      validator: async (/* _, names */): Promise<void> => {
        if (lastError.hasError !== true) {
          if (validatorFunc != null) {
            validatorFunc()
          }
          return Promise.resolve()
        }
        return Promise.reject(new Error(message))
      },
    }

    newRules.push(newValidator)
  }

  attrs.rules = newRules

  const onChange = (e: any) => {
    console.log('onChange ~!!', e)
  }

  const onError = (e: { hasError: boolean }) => {
    console.log('onError ~!!', e)
    lastError = e
  }

  return (
    <OmFormItem
      label={info.editLabel}
      name={info.name}
      key={info.name}
      valuePropName="value"
      {...attrs}
    >
      <JsonEditorChooser
        style={inputAttrs.style}
        onError={onError}
        onChange={onChange}
        options={options}
        inputAttrs={inputAttrs}
      />
    </OmFormItem>
  )
}

function createHidden(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)

  return (
    // hidden 필드: 값 등록만(setFieldsValue/getFieldsValue 대상), 표시 없음. onChange 미발화(사용자 입력 불가).
    <OmFormItem label={info.editLabel} name={info.name} key={info.name} {...attrs} noStyle>
      <input type="hidden" {...inputAttrs} />
    </OmFormItem>
  )
}

function createCheckbox(
  info: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement {
  const { attrs, inputAttrs } = getAttrs(info, reactObj, value, values)
  const children = <OmCheckbox {...inputAttrs} />

  return (
    <OmFormItem
      label={info.editLabel}
      name={info.name}
      key={info.name}
      valuePropName="checked"
      {...attrs}
    >
      {children}
    </OmFormItem>
  )
}

function createSelect(
  info: FieldInfo,
  reactObj: ReactObj | null,
  val: any,
  values: any,
  isNumberType: boolean,
): ReactElement {
  // const onChange = info.options ? info.options.onChange : null
  const i18nT = info.options && info.options.t ? info.options.t : null

  const { attrs, inputAttrs } = getAttrs(info, reactObj, val, values)

  const iAttrs = inputAttrs || {}

  const source = i18nT && info.origin.valueKeys ? info.origin.valueKeys : info.origin.values
  const options: OmSelectOption<string | number>[] = Object.entries(source).map(([key, value]) => ({
    value: isNumberType === true ? parseInt(key, 10) : key,
    label: i18nT ? i18nT(value as string) : (value as string),
  }))

  return (
    <OmFormItem label={info.editLabel} name={info.name} key={info.name} {...attrs}>
      <OmSelect placeholder={info.desc} options={options} {...iAttrs} />
    </OmFormItem>
  )
}

function createRadio(
  info: FieldInfo,
  reactObj: ReactObj | null,
  val: any,
  values: any,
): ReactElement {
  const i18nT = info.options && info.options.t ? info.options.t : null

  const { attrs, inputAttrs } = getAttrs(info, reactObj, val, values)

  const source = i18nT && info.origin.valueKeys ? info.origin.valueKeys : info.origin.values
  const options: ReactElement[] = Object.entries(source).map(([key, value]) => (
    <OmRadio value={key} key={key}>
      {i18nT ? i18nT(value as string) : (value as string)}
    </OmRadio>
  ))

  return (
    <OmFormItem label={info.editLabel} name={info.name} key={info.name} {...attrs}>
      <OmRadioGroup name={info.name} {...inputAttrs}>
        {options}
      </OmRadioGroup>
    </OmFormItem>
  )
}

function getBase64(img: File | Blob, callback: (result: string) => void): void {
  const reader = new FileReader()
  reader.addEventListener('load', () => callback(reader.result as string))
  reader.readAsDataURL(img)
}

interface NgqlImageUploadProps {
  imageUrl: string | null
  loading: boolean
  alt?: string
  uploadBtn?: ReactNode
  beforeUpload: (file: File) => void
  onPick: (file: File) => void
  // OmFormItem(valuePropName="file")이 cloneElement 로 주입하는 트리거.
  onChange?: (file: File) => void
}

// native <input type=file> 아바타 업로더. OmFormItem 직접 자식이라 주입 onChange 를 받는다.
function NgqlImageUpload({
  imageUrl,
  loading,
  alt,
  uploadBtn,
  beforeUpload,
  onPick,
  onChange,
}: NgqlImageUploadProps): ReactElement {
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // 동일 파일 재선택 허용
    if (file == null) {
      return
    }
    beforeUpload(file)
    onPick(file)
    onChange?.(file)
  }

  return (
    <label className="avatar-uploader nine-image-upload">
      {imageUrl != null ? (
        <img src={imageUrl} alt={alt} style={{ width: '100%' }} />
      ) : (
        <div className="nine-image-upload-btn">
          {loading ? <OmIcon name="loading" /> : <OmIcon name="plus" />}
          <div style={{ marginTop: 8 }}>{uploadBtn}</div>
        </div>
      )}
      <input type="file" style={{ display: 'none' }} onChange={handleInput} />
    </label>
  )
}

function createImageUpload(
  info: FieldInfo,
  reactObj: ReactObj,
  value: any,
  values: any,
): ReactElement {
  const { attrs } = getAttrs(info, reactObj, value, values)

  const onCheckfile = info.origin ? info.origin.onCheckfile : null

  const { options, origin } = info

  const alt = options.t && origin.altKey ? options.t(origin.altKey) : origin.alt
  const uploadBtn =
    options.t && origin.uploadBtnKey ? options.t(origin.uploadBtnKey) : origin.uploadBtn

  const beforeUpload = (file: File): void => {
    if (onCheckfile != null) {
      const result = onCheckfile(file)
      if (result === true) {
        ;(file as any).status = 'fake-done'
      }
    }
  }

  if (reactObj._ngqlObj == null) {
    reactObj._ngqlObj = {}
  }

  if (reactObj._ngqlObj[info.name] == null) {
    reactObj._ngqlObj[info.name] = {
      imageUrl: null,
      loading: false,
    }
  }

  const onPick = (file: File): void => {
    getBase64(file, (imageUrl) => {
      reactObj._ngqlObj![info.name] = {
        imageUrl,
        loading: false,
      }
      if (reactObj != null) {
        reactObj.forceUpdate!()
      }
    })
  }

  return (
    // valuePropName="file": OmFormItem(RHF Controller)이 주입하는 onChange 를 NgqlImageUpload 가 직접 수신.
    <OmFormItem
      label={info.editLabel}
      name={info.name}
      key={info.name}
      valuePropName="file"
      {...attrs}
    >
      <NgqlImageUpload
        imageUrl={reactObj._ngqlObj[info.name].imageUrl}
        loading={reactObj._ngqlObj[info.name].loading}
        alt={alt}
        uploadBtn={uploadBtn}
        beforeUpload={beforeUpload}
        onPick={onPick}
      />
    </OmFormItem>
  )
}

function createEditElem(
  compInfo: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement | string {
  if (compInfo.origin.mode === 'readonly') {
    // return <React.Fragment>{attrs.initialValue}</React.Fragment>
    return ''
  }

  let comp: ReactElement | null = null
  switch (compInfo.type) {
    case 'custom':
      if (compInfo.origin.createEditElem != null) {
        comp = compInfo.origin.createEditElem(compInfo, reactObj, value, NineGQLUI, values)
      } else {
        console.log('not fount - origin.createEditElem')
      }
      break
    case 'select':
      comp = createSelect(compInfo, reactObj, value, values, false)
      break
    case 'select-number':
      comp = createSelect(compInfo, reactObj, value, values, true)
      break
    case 'radio':
      comp = createRadio(compInfo, reactObj, value, values)
      break
    case 'checkbox':
      comp = createCheckbox(compInfo, reactObj, value, values)
      break
    case 'hidden':
      comp = createHidden(compInfo, reactObj, value, values)
      break
    case 'textarea':
    case 'formattedtext':
      comp = createTextarea(compInfo, reactObj, value, values)
      break
    case 'long-json':
    case 'json':
      comp = createJSONEditor(compInfo, reactObj, value, values)
      break
    case 'schema-json':
      comp = createSchemaJSONEditor(compInfo, reactObj, value, values)
      break
    case 'json2':
      comp = createJSONEditorChooser(compInfo, reactObj, value, values)
      break
    case 'image':
      comp = createImageUpload(compInfo, reactObj as ReactObj, value, values)
      break
    case 'text':
    case 'number':
    case 'url':
    case 'email':
    case 'datetime':
    default:
      comp = createInput(compInfo, reactObj, value, values)
  }

  return comp || ''
}

interface ChildrenObj {
  getNodes: () => ReactElement[]
}

function createEditContainer(
  childrenObj: ChildrenObj,
  options: any,
  uiProps: any,
  uiAttrs: any,
  formRef: React.RefObject<OmFormInstance>,
  btnsUI: ReactElement | null,
): ReactElement | null {
  let attrs: any = {}
  if (options.layout != null) {
    attrs.layout = options.layout
  }
  if (options.onFinish != null) {
    attrs.onFinish = options.onFinish
  }

  if (uiAttrs != null && uiAttrs.formAttrs != null) {
    attrs = {
      ...attrs,
      ...uiAttrs.formAttrs,
    }
  }

  if (options.formAttrs != null) {
    attrs = extend(attrs, options.formAttrs)
  }

  const labelComp = createTitleElem(uiProps, options)

  const nodeElems = childrenObj.getNodes()

  if ((labelComp == null || (labelComp as any).title == null) && nodeElems.length === 0) {
    return null
  }

  return (
    <React.Fragment>
      {labelComp}
      <OmForm ref={formRef} {...attrs}>
        {childrenObj.getNodes()}
        {btnsUI}
      </OmForm>
    </React.Fragment>
  )
}

function createDetailElem(
  compInfo: FieldInfo,
  reactObj: ReactObj | null,
  value: any,
  values: any,
): ReactElement | string {
  const { options, origin: uiProps, value: val } = compInfo

  let children: ReactNode = ''

  if (compInfo.origin.mode === 'none' || compInfo.type === 'none') {
    return ''
  }

  if (compInfo.type === 'custom') {
    // custom type 추가.
    if (compInfo.origin.createDetailElem != null) {
      children = compInfo.origin.createDetailElem(compInfo, reactObj, value, NineGQLUI, values)
    } else {
      console.log('not fount - origin.createDetailElem')
    }
  } else if (compInfo.type === 'datetime-utc' || compInfo.type === 'datetime-ufw-utc') {
    children = val
  } else if (compInfo.type === 'datetime' || compInfo.type === 'datetime-ufw') {
    children = val
  } else if (compInfo.type === 'date' || compInfo.type === 'date-ufw') {
    children = val
  } else if (compInfo.type === 'date-utc' || compInfo.type === 'date-ufw-utc') {
    children = val
  } else if (compInfo.type === 'time' || compInfo.type === 'time-ufw') {
    children = val
  } else if (compInfo.type === 'time-utc' || compInfo.type === 'time-ufw-utc') {
    children = val
  } else if (compInfo.type === 'url') {
    children = (
      <a className="ngql-link" href={val} target="_blank" rel="noopener noreferrer">
        {val}
      </a>
    )
  } else if (compInfo.type === 'checkbox') {
    children = val ? 'true' : ' false'
  } else if (
    compInfo.type === 'select' ||
    compInfo.type === 'select-number' ||
    compInfo.type === 'radio'
  ) {
    const selectKey = val
    if (options.t && compInfo.origin.valueKeys != null) {
      children = options.t(compInfo.origin.valueKeys[selectKey])
    } else {
      children = compInfo.origin.values[selectKey]
    }
  } else if (compInfo.type === 'image') {
    const alt = options.t && uiProps.altKey ? options.t(uiProps.altKey) : uiProps.alt
    // const { src } = uiProps
    children = <img className="ngql-image" src={val} alt={alt} />
  } else if (compInfo.type === 'formattedtext') {
    children = <pre>{str(val)}</pre>
  } else if (
    compInfo.type === 'json' ||
    compInfo.type === 'json2' ||
    compInfo.type === 'long-json' ||
    compInfo.type === 'schema-json'
  ) {
    let obj = val

    if (typeof val === 'string') {
      const jsonVal = val != null && val.length > 0 ? val : '{}'
      obj = JSON.parse(jsonVal)
    }

    let jsonStr = JSON.stringify(obj, null, 2)

    let showBtn: ReactElement | string = ''

    if (
      compInfo.type === 'long-json' ||
      compInfo.type === 'schema-json' ||
      compInfo.type === 'json2'
    ) {
      const originStr = jsonStr

      const limit = (uiProps.lineLimit || 10) - 1
      const regex = new RegExp(`(((.*?)(\\r|\\n|\\r\\n)){0,${limit}}(.*)(\\r|\\n|\\r\\n)?)`)

      const found = jsonStr.match(regex)

      if (found) {
        ;[jsonStr] = found
      }

      if (originStr.length !== jsonStr.length) {
        const modalState = reactObj?.state?.[`${compInfo.name}FieldModal`]
        const showModal = () => {
          const state: any = {}
          state[`${compInfo.name}FieldModal`] = true
          reactObj?.setState!(state)
        }
        const hideModal = () => {
          const state: any = {}
          state[`${compInfo.name}FieldModal`] = false
          reactObj?.setState!(state)
        }
        showBtn = (
          <React.Fragment>
            ...
            <br />
            <OmButton variant="primary" onClick={showModal}>
              Show All
            </OmButton>
            <OmDialog
              className="code-view"
              title={compInfo.label}
              open={modalState}
              onClose={hideModal}
              footer={
                <OmButton variant="primary" onClick={hideModal}>
                  OK
                </OmButton>
              }
            >
              <pre>
                <code>{str(originStr)}</code>
              </pre>
            </OmDialog>
          </React.Fragment>
        )
      }
    }
    children = (
      <React.Fragment>
        <pre>
          <code>{str(jsonStr)}</code>
        </pre>
        {showBtn}
      </React.Fragment>
    )
  } else {
    children = str(val)
  }

  const descAttrs = createTitleAttrs(uiProps, options)

  // const afterText = compInfo.afterText ? compInfo.afterText : ''

  const comp = (
    <OmDescriptionsItem label={compInfo.label} key={compInfo.name} {...descAttrs}>
      {children}
    </OmDescriptionsItem>
  )

  return comp
}

function createDetailContainer(
  childrenObj: ChildrenObj,
  options: any,
  uiProps: any,
  uiAttrs: any,
): ReactElement {
  let descAttrs = createTitleAttrs(uiProps, options)

  descAttrs = extend(descAttrs, uiAttrs)

  return (
    <OmDescriptions size="small" column={1} bordered {...descAttrs}>
      {childrenObj.getNodes()}
    </OmDescriptions>
  )
}

interface TableInfo {
  onRow?: (record: any, rowIndex: number, reactObj: ReactObj | null) => any
  mobileInfo?: {
    foldable?: boolean | { icon?: any }
  }
  getKeyFunc: (record: any) => string
  moreBtnText?: string
  useMobileMode?: boolean
}

interface OnRowResult {
  onClick?: (e: React.MouseEvent) => boolean | void
  onMouseEnter?: (e: React.MouseEvent) => void
  onMouseLeave?: (e: React.MouseEvent) => void
  onMouseDown?: (e: React.MouseEvent) => void
}

const getTableOnRow = (name: string, tableInfo: TableInfo, reactObj: ReactObj | null) => {
  return (record: any, rowIndex: number): OnRowResult => {
    let originOnRow: OnRowResult | null = null
    let onRow: OnRowResult | null = null
    if (reactObj != null) {
      const onRowFunc = reactObj[`onRow${name}`]
      if (onRowFunc != null) {
        originOnRow = onRowFunc(record, rowIndex)
      }
    }

    if (tableInfo.onRow != null) {
      onRow = tableInfo.onRow(record, rowIndex, reactObj)
    }

    const rowInfo: any = {}
    if ((ngqlConfig as any).tableClickRange != null && (ngqlConfig as any).tableClickRange > 0) {
      rowInfo.tableClickRange = (ngqlConfig as any).tableClickRange
    }

    const onRowAttr: OnRowResult = {
      onClick: (e: React.MouseEvent) => {
        // console.log(' - click ', e, record, rowIndex)

        if (rowInfo.tableClickRange > 0) {
          const diff = rowInfo.tableClickRange
          const cX = (e as any).clientX
          const cY = (e as any).clientY

          if (
            cX - diff > rowInfo.cX ||
            rowInfo.cX > cX + diff ||
            cY - diff > rowInfo.cY ||
            rowInfo.cY > cY + diff
          ) {
            e.preventDefault()
            e.stopPropagation()
            return
          }
        }

        if (
          tableInfo.mobileInfo != null &&
          tableInfo.mobileInfo.foldable != null &&
          tableInfo.mobileInfo.foldable !== false
        ) {
          const icon = (tableInfo.mobileInfo.foldable as any).icon

          const targetPos = (e.currentTarget as HTMLElement).getBoundingClientRect()

          const top = targetPos.y + icon.top
          const bottom = targetPos.y + icon.top + icon.height

          const left = targetPos.right - icon.right - icon.width
          const right = targetPos.right - icon.right

          if (
            (e as any).clientX > left &&
            (e as any).clientX < right &&
            (e as any).clientY > top &&
            (e as any).clientY < bottom
          ) {
            const classes = (e.currentTarget as HTMLElement).className.split(' ')
            const idx = classes.indexOf('unfold')
            if (idx > -1) {
              classes.splice(idx, 1)
            } else {
              classes.push('unfold')
            }
            ;(e.currentTarget as HTMLElement).className = classes.join(' ')
            e.preventDefault()
            e.stopPropagation()
            return
          }
        }

        if (originOnRow != null && originOnRow.onClick) {
          if (originOnRow.onClick(e) === true) {
            return
          }
        }
        if (onRow != null && onRow.onClick != null) {
          onRow.onClick(e)
        }
      }, // click row
      // onDoubleClick: () => {}, // double click row
      // onContextMenu: () => {}, // right button click row
      onMouseEnter: (e: React.MouseEvent) => {
        if (onRow != null && onRow.onMouseEnter != null) {
          onRow.onMouseEnter(e)
        }
      }, // mouse enter row
      onMouseLeave: (e: React.MouseEvent) => {
        if (onRow != null && onRow.onMouseLeave != null) {
          onRow.onMouseLeave(e)
        }
      }, // mouse leave row
    }

    if (rowInfo.tableClickRange > 0) {
      rowInfo.cX = -1
      rowInfo.cY = -1
      onRowAttr.onMouseDown = (e: React.MouseEvent) => {
        // console.log(' - onMouseDown ', e, record, rowIndex)
        rowInfo.cX = (e as any).clientX
        rowInfo.cY = (e as any).clientY
      }
    }

    return onRowAttr
  }
}

interface Node {
  fields?: Record<string, any>
  uiProps?: any
}

interface ColumnInfo {
  columns: OmTableColumn<any>[]
  rowSelection?: any
}

const getTableAttrs = (
  node: Node,
  props: any,
  tableInfo: TableInfo,
  columnInfo: ColumnInfo,
  dataSource: any[],
  resObj: ResultObjContract,
) => {
  const columns = columnInfo.columns.map((oldColumn) => {
    const column: any = {
      ...oldColumn,
    }
    const oldRender = column.render
    column.render = (text: any, record: any) => {
      let columnKey: string | null = null
      if (Array.isArray(column.dataIndex)) {
        columnKey = column.dataIndex[column.dataIndex.length - 1]
      } else {
        columnKey = column.dataIndex
      }

      let title: string | null = column.title
      if (column.mobileTitle != null) {
        const tmp = removeTag(column.mobileTitle.trim())
        title = tmp.length > 0 ? tmp : null
      } else if (typeof column.title === 'string') {
        const tmp = removeTag(column.title.trim())
        title = tmp.length > 0 ? tmp : null
      }

      let textElem = text

      if (
        node.fields != null &&
        node.fields[columnKey!] != null &&
        node.fields[columnKey!].nineType === 'field'
      ) {
        textElem = (
          NineGQLUI.generateDetailUIByKey as (
            ...args: unknown[]
          ) => { detail?: unknown } | null | undefined
        )(columnKey!, node.fields[columnKey!], record.node, props, resObj.reactObj, null)

        textElem = textElem != null ? textElem.detail : text
      }

      const renderResult = oldRender(textElem, record)

      const oldElem =
        renderResult != null &&
        typeof renderResult !== 'string' &&
        React.isValidElement(renderResult) !== true
          ? renderResult.children
          : renderResult

      if (oldElem == null) {
        return ''
      }

      const titleElem =
        title != null ? (
          <div className="nine-column-title">{title} : </div>
        ) : (
          <div className="nine-column-title"> </div>
        )

      const newElem = (
        <div className="nine-table-item-column" key={`item-${columnKey}`}>
          {titleElem}
          <div className="nine-column-value">{oldElem}</div>
        </div>
      )

      if (renderResult.children != null) {
        renderResult.children = newElem
        return renderResult
      }

      return newElem
    }
    return column
  })

  const attrs: any = {
    onRow: getTableOnRow(resObj.name, tableInfo, resObj.reactObj),
    loading: resObj.getLoading(),
    columns,
    dataSource,
    rowKey: tableInfo.getKeyFunc,
  }

  if (columnInfo.rowSelection != null) {
    attrs.rowSelection = columnInfo.rowSelection
  }

  if (props.rowClassName != null) {
    attrs.rowClassName = props.rowClassName
  }

  const pageCtrl = resObj.getPageControl()

  const onLoadMore = () => {
    if (resObj.morePage != null) {
      resObj.morePage()
    }
  }

  if (pageCtrl === 'more') {
    const nodeData = resObj.getResult?.()[0] as { hasNext?: boolean } | undefined
    if (nodeData != null && nodeData.hasNext === true) {
      console.log(' more button ~~! ')
      const btnText = tableInfo.moreBtnText != null ? tableInfo.moreBtnText : 'loading more'
      const loadMore = (
        <div className="more-button-container">
          <OmButton onClick={onLoadMore}>{btnText}</OmButton>
        </div>
      )
      attrs.loadMore = loadMore
    }
    attrs.pagination = false
  } else if (pageCtrl !== false) {
    attrs.pagination = resObj.getPaginationInfo()
  } else {
    attrs.pagination = false
  }

  let clsNames: string[] = []
  if (props.classNames != null) {
    clsNames = clsNames.concat(props.classNames)
  }

  if (tableInfo.useMobileMode !== false) {
    // attrs.style = createCSSLabel(columns)
    clsNames.push('useMobileMode')
  }

  if (
    tableInfo.mobileInfo != null &&
    tableInfo.mobileInfo.foldable != null &&
    tableInfo.mobileInfo.foldable !== false
  ) {
    // attrs.style = createCSSLabel(columns)
    if (tableInfo.mobileInfo.foldable === true) {
      ;(tableInfo.mobileInfo.foldable as any).icon = {}
    }

    const icon = (tableInfo.mobileInfo.foldable as any).icon
    const style: any = {}

    if (icon != null) {
      // 좌표 계산을 위해 초기값 설정 필요. 기본 CSS값과 동일하게 맞춰야 함.
      if (icon.width == null) {
        icon.width = 24
      }
      if (icon.height == null) {
        icon.height = 24
      }
      if (icon.top == null) {
        icon.top = 9
      }
      if (icon.right == null) {
        icon.right = 4
      }

      Object.entries(icon).forEach(([key, value]) => {
        style[`--foldicon-${key}`] = `${value}px`
      })
    }
    attrs.style = style
    clsNames.push('foldable')
  }

  if (clsNames.length > 0) {
    attrs.className = clsNames.join(' ')
  }

  if (resObj.getOnChange) {
    attrs.onChange = resObj.getOnChange()

    if (attrs.pagination != null) {
      delete attrs.pagination.onChange
    }
  }

  if (props.attrs != null) {
    extend(attrs, props.attrs)
  }

  return attrs
}

function createTableInfo(
  node: Node,
  options: any,
  tableInfo: TableInfo,
  columnInfo: ColumnInfo,
  dataSource: any[],
  resObj: ResultObjContract,
) {
  const attrs = getTableAttrs(node, options, tableInfo, columnInfo, dataSource, resObj)
  return attrs
}

function createTableElem(
  node: Node,
  options: any,
  tableInfo: TableInfo,
  columnInfo: ColumnInfo,
  dataSource: any[],
  resObj: ResultObjContract,
): ReactElement {
  const { uiProps } = node
  const descElem = createTitleElem(uiProps, options)

  if (node.uiProps != null) {
    options.uiNodeProps = node.uiProps
  }

  const attrs = getTableAttrs(node, options, tableInfo, columnInfo, dataSource, resObj)

  const onSizeChange = (isMobileView: boolean) => {
    console.log(' -- onSizeChange - ', isMobileView)

    resObj.setMobileMode!(isMobileView)

    if (resObj.movePage != null) {
      const defaultV = resObj.getDefaultVariable!()
      const pageNum = resObj.getArgument!(defaultV, 'pageNum', 1) as number

      resObj.movePage(pageNum)
    }
  }

  return (
    <DeviceSizeChecker onSizeChange={onSizeChange}>
      {descElem}
      {/* loadMore 는 attrs 에 실려 있으나 OmTable prop 아님 → 아래서 별도 렌더(antd Table 때와 동일). */}
      <OmTable scroll={{ x: '100%' }} {...attrs} />
      {attrs.loadMore}
    </DeviceSizeChecker>
  )
}

interface ItemInfo {
  action?: any[]
  header?: ReactElement | null
  footer?: ReactElement
  columns?: any[]
  render?: (record: any) => ReactElement
}

const getListAttrs = (
  node: Node,
  props: any,
  tableInfo: TableInfo,
  itemInfo: ItemInfo,
  dataSource: any[],
  resObj: ResultObjContract,
) => {
  const attrs: any = {
    loading: resObj.getLoading(),
    dataSource,
    rowKey: tableInfo.getKeyFunc,
  }

  const onLoadMore = () => {
    if (resObj.morePage != null) {
      resObj.morePage()
    }
  }

  const pageCtrl = resObj.getPageControl()

  if (pageCtrl === 'more') {
    // const nodeData = resObj.getResult()[0];
    console.log(' more button ~~! ')
    const btnText = tableInfo.moreBtnText != null ? tableInfo.moreBtnText : 'loading more'
    const loadMore = (
      <div className="more-button-container">
        <OmButton onClick={onLoadMore}>{btnText}</OmButton>
      </div>
    )

    attrs.loadMore = loadMore
    attrs.pagination = false
  } else if (pageCtrl !== false) {
    attrs.pagination = resObj.getPaginationInfo()
  } else {
    attrs.pagination = false
  }

  let clsNames = ['nine-list']
  if (props.classNames != null) {
    clsNames = clsNames.concat(props.classNames)
  }

  if (tableInfo.useMobileMode !== false) {
    clsNames.push('useMobileMode')
  }

  if (
    tableInfo.mobileInfo != null &&
    tableInfo.mobileInfo.foldable != null &&
    tableInfo.mobileInfo.foldable !== false
  ) {
    // attrs.style = createCSSLabel(columns)
    const icon = (tableInfo.mobileInfo.foldable as any).icon
    const style: any = {}

    if (icon != null) {
      Object.entries(icon).forEach(([key, value]) => {
        style[`--foldicon-${key}`] = `${value}px`
      })
    }
    attrs.style = style
    clsNames.push('foldable')
  }

  if (clsNames.length > 0) {
    attrs.className = clsNames.join(' ')
  }

  const onRowFunc = getTableOnRow(resObj.name, tableInfo, resObj.reactObj)

  const itemAttr: any = {}

  if (itemInfo.action != null) {
    itemAttr.action = itemInfo.action
  }

  if (itemInfo.header != null) {
    attrs.header = itemInfo.header
  } else if (itemInfo.header === undefined && itemInfo.columns != null) {
    let idx = 0
    const headerItems = itemInfo.columns.map((item) => {
      idx += 1

      const headerKey = `header-${idx}`
      const headerAttrs: any = {
        className: 'nine-list-header-column',
      }

      const style: any = {}
      if (item.width != null) {
        if (typeof item.width === 'number') {
          style['--item-width'] = `${item.width}px`
        } else {
          style['--item-width'] = item.width
        }
      }

      if (item.align != null) {
        style['--item-align'] = item.align
      }

      headerAttrs.style = style

      return (
        <div key={headerKey} {...headerAttrs}>
          {item.title}
        </div>
      )
    })

    attrs.header = <div className="nine-list-header">{headerItems}</div>

    attrs.footer = itemInfo.footer != null ? itemInfo.footer : <div className="nine-list-footer" />
  }

  let expandable: any = null
  if (props.attrs != null && props.attrs.expandable != null) {
    expandable = props.attrs.expandable
  }

  attrs.renderItem = (record: any) => {
    let itemElem: ReactElement | null = null

    if (itemInfo.render != null) {
      itemElem = itemInfo.render(record)
    } else {
      const items = itemInfo.columns!.map((item) => {
        let text = record
        let columnKey: string | null = null
        if (Array.isArray(item.dataIndex)) {
          item.dataIndex.forEach((val: string) => {
            text = text[val]
          })
          columnKey = item.dataIndex[item.dataIndex.length - 1]
        } else {
          text = text[item.dataIndex]
          columnKey = item.dataIndex
        }
        const itemColumnKey = `item-${columnKey}`
        const itemAttrs: any = {
          className: 'nine-list-item-column',
        }

        const style: any = {}
        if (item.width != null) {
          if (typeof item.width === 'number') {
            style['--item-width'] = `${item.width}px`
          } else {
            style['--item-width'] = item.width
          }
        }

        if (item.align != null) {
          style['--item-align'] = item.align
        }

        itemAttrs.style = style

        let title: string | null = item.title
        if (item.mobileTitle != null) {
          const tmp = removeTag(item.mobileTitle.trim())
          title = tmp.length > 0 ? tmp : null
        } else if (typeof item.title === 'string') {
          const tmp = removeTag(item.title.trim())
          title = tmp.length > 0 ? tmp : null
        }

        const titleElem =
          title != null ? (
            <div className="nine-column-title">{title} : </div>
          ) : (
            <div className="nine-column-title"> </div>
          )

        return (
          <div key={itemColumnKey} {...itemAttrs}>
            {titleElem}
            <div className="nine-column-value">{item.render(text, record)}</div>
          </div>
        )
      })

      if (expandable != null && expandable.expandedRowRender != null) {
        const extItem = expandable.expandedRowRender(record)
        itemElem = (
          <React.Fragment>
            <div className="nine-list-item">{items}</div>
            <div className="nine-list-item-ext">{extItem}</div>
          </React.Fragment>
        )
      } else {
        itemElem = <div className="nine-list-item">{items}</div>
      }
    }

    const funcMap = onRowFunc(record, -1)
    itemAttr.onClick = (e: React.MouseEvent) => {
      funcMap.onClick!(e)
    }

    if (funcMap.onMouseDown != null) {
      itemAttr.onMouseDown = (e: React.MouseEvent) => {
        funcMap.onMouseDown!(e)
      }
    }

    return <OmListItem {...itemAttr}>{itemElem}</OmListItem>
  }

  if (props.attrs != null) {
    extend(attrs, props.attrs)
  }

  return attrs
}

function createListInfo(
  node: Node,
  options: any,
  tableInfo: TableInfo,
  itemInfo: ItemInfo,
  dataSource: any[],
  resObj: ResultObjContract,
) {
  const attrs = getListAttrs(node, options, tableInfo, itemInfo, dataSource, resObj)
  return attrs
}

function createListElem(
  node: Node,
  options: any,
  tableInfo: TableInfo,
  itemInfo: ItemInfo,
  dataSource: any[],
  resObj: ResultObjContract,
): ReactElement {
  const { uiProps } = node
  const descElem = createTitleElem(uiProps, options)

  if (node.uiProps != null) {
    options.uiNodeProps = node.uiProps
  }

  const attrs = getListAttrs(node, options, tableInfo, itemInfo, dataSource, resObj)

  const onSizeChange = (isMobileView: boolean) => {
    console.log(' -- onSizeChange - ', isMobileView)

    resObj.setMobileMode!(isMobileView)

    if (resObj.movePage != null) {
      const defaultV = resObj.getDefaultVariable!()
      const pageNum = resObj.getArgument!(defaultV, 'pageNum', 1) as number

      resObj.movePage(pageNum)
    }
  }

  // attrs 는 antd List 계약(loading:boolean/pagination 객체) → OmList 표면으로 매핑.
  const pagination = attrs.pagination
  return (
    <DeviceSizeChecker onSizeChange={onSizeChange}>
      {descElem}
      <OmList
        className={attrs.className}
        style={attrs.style}
        dataSource={attrs.dataSource}
        renderItem={attrs.renderItem}
        header={attrs.header}
        footer={attrs.footer}
        loading={{ spinning: attrs.loading === true }}
      />
      {attrs.loadMore}
      {pagination !== false && pagination != null && (
        <OmPagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={pagination.total}
          showSizeChanger={pagination.showSizeChanger}
          onChange={(page) => pagination.onChange?.(page)}
        />
      )}
    </DeviceSizeChecker>
  )
}

function createTitleAttrs(uiProps: any, options: any): any {
  let label: ReactNode = null
  if (uiProps != null) {
    label = options.t && uiProps.labelKey ? options.t(uiProps.labelKey) : uiProps.label
  }
  if (options.title != null) {
    label = options.title
  }

  let descAttrs: any = null
  if (label != null || options.extra != null || options.descAttrs != null) {
    descAttrs = {}
    if (label != null) {
      descAttrs.title = label
    }
    if (options.extra != null) {
      descAttrs.extra = options.extra
    }
    if (options.descAttrs != null) {
      extend(descAttrs, options.descAttrs)
    }
  }

  return descAttrs
}

function createTitleElem(uiProps: any, options: any): ReactElement | string {
  const descAttrs = createTitleAttrs(uiProps, options)

  if (descAttrs != null) {
    return <OmDescriptions size="small" column={1} bordered {...descAttrs} />
  }
  return ''
}

interface CompInfo {
  nodeInfo: {
    list: any[]
    map: Record<string, { isTable?: boolean }>
  }
  getElem: (key: string | null, uiAttrs: any) => ReactElement | null
}

function createAllElem(compInfo: CompInfo, uiAttrs: any): ReactElement {
  const subElems: Array<{ key: string; elem: ReactElement }> = []

  if (compInfo.nodeInfo.list.length > 0) {
    Object.keys(compInfo.nodeInfo.map).forEach((key) => {
      if (compInfo.nodeInfo.map[key].isTable !== true) {
        const elem = compInfo.getElem(key, uiAttrs)
        if (elem != null) {
          subElems.push({
            key,
            elem,
          })
        }
      }
    })
  }

  const mainFields = compInfo.getElem(null, uiAttrs)

  const mainElem =
    mainFields != null ? (
      <React.Fragment>
        {mainFields}
        {/* <br /> */}
      </React.Fragment>
    ) : null

  return (
    <React.Fragment>
      {mainElem}
      {subElems.map((item /* , idx */) => {
        return (
          <React.Fragment key={item.key}>
            {/* idx > 0 ? <br /> : null */}
            <br />
            {item.elem}
          </React.Fragment>
        )
      })}
    </React.Fragment>
  )
}

function valueSetFilter(isEdit: boolean, type: string, value: string): string {
  // console.log(' --- vSetf : ', isEdit, type, value)
  if (isEdit === true) {
    // detail, edit에는 local time으로 표시.
    // 저장은 utc로
    if (type === 'datetime' || type === 'datetime-ufw') {
      return timeFormat(value, 'YYYY-MM-DDTHH:mm')
    }
    // detail, edit에는 utc time으로 표시.
    // 저장도 utc로
    if (type === 'datetime-utc' || type === 'datetime-ufw-utc') {
      return timeFormatUTC(value, 'YYYY-MM-DDTHH:mm')
    }

    if (type === 'date' || type === 'date-ufw') {
      return timeFormat(value, 'YYYY-MM-DD')
    }
    if (type === 'date-utc' || type === 'date-ufw-utc') {
      return timeFormatUTC(value, 'YYYY-MM-DD')
    }
    if (type === 'time' || type === 'time-ufw') {
      return timeFormat(value, 'HH:mm')
    }
    if (type === 'time-utc' || type === 'time-ufw-utc') {
      return timeFormatUTC(value, 'HH:mm')
    }
  } else {
    if (type === 'datetime' || type === 'datetime-ufw') {
      return `${timeFormatDefault(value)} ${timeFormat(value, 'ZZ')}`
    }
    if (type === 'datetime-utc' || type === 'datetime-ufw-utc') {
      return `${timeFormatUTCDefault(value)} ${timeFormatUTC(value, 'ZZ')}`
    }

    if (type === 'date' || type === 'date-ufw') {
      return timeFormat(value, 'YYYY-MM-DD ZZ')
    }
    if (type === 'date-utc' || type === 'date-ufw-utc') {
      return timeFormatUTC(value, 'YYYY-MM-DD ZZ')
    }
    if (type === 'time' || type === 'time-ufw') {
      return timeFormat(value, 'HH:mm ZZ')
    }
    if (type === 'time-utc' || type === 'time-ufw-utc') {
      return timeFormatUTC(value, 'HH:mm ZZ')
    }
  }
  return value
}

function valueGetFilter(isEdit: boolean, type: string, value: string): string {
  // console.log(' --- vGetf : ', isEdit, type, value)
  if (isEdit === true) {
    if (type === 'datetime' || type === 'datetime-ufw') {
      return moment(value).utc().format('YYYY-MM-DDTHH:mm')
    }
    if (type === 'datetime-utc' || type === 'datetime-ufw-utc') {
      const time = value
      let utcTime = time
      // if (time.length != null && time.indexOf('T') > 0 && time.indexOf('Z') === -1) {
      if (time.length != null && time.indexOf('Z') === -1) {
        utcTime = `${time}Z`
      }
      return moment(utcTime).utc().format('YYYY-MM-DDTHH:mm')
    }

    if (type === 'date' || type === 'date-ufw') {
      return moment(value).utc().format('YYYY-MM-DDTHH:mm')
    }
    if (type === 'date-utc' || type === 'date-ufw-utc') {
      const time = `${value}T00:00:00`
      let utcTime = time
      // if (time.length != null && time.indexOf('T') > 0 && time.indexOf('Z') === -1) {
      if (time.length != null && time.indexOf('Z') === -1) {
        utcTime = `${time}Z`
      }
      return moment(utcTime).utc().format('YYYY-MM-DDTHH:mm')
    }
    if (type === 'time' || type === 'time-ufw') {
      return moment(value).utc().format('HH:mm')
    }
    if (type === 'time-utc' || type === 'time-ufw-utc') {
      const time = value
      let utcTime = time
      // if (time.length != null && time.indexOf('T') > 0 && time.indexOf('Z') === -1) {
      if (time.length != null && time.indexOf('Z') === -1) {
        utcTime = `${time}Z`
      }
      return moment(utcTime).utc().format('HH:mm')
    }
  }
  return value
}

const NineGQLUI: NineGQLUIAdapter = {
  getAttrs: getAttrs as unknown as NineGQLUIAdapter['getAttrs'],
  createEditElem: createEditElem as unknown as NineGQLUIAdapter['createEditElem'],
  createEditContainer: createEditContainer as unknown as NineGQLUIAdapter['createEditContainer'],
  createDetailElem: createDetailElem as unknown as NineGQLUIAdapter['createDetailElem'],
  createDetailContainer:
    createDetailContainer as unknown as NineGQLUIAdapter['createDetailContainer'],
  createTableInfo: createTableInfo as unknown as NineGQLUIAdapter['createTableInfo'],
  createTableElem: createTableElem as unknown as NineGQLUIAdapter['createTableElem'],
  createListInfo: createListInfo as unknown as NineGQLUIAdapter['createListInfo'],
  createListElem: createListElem as unknown as NineGQLUIAdapter['createListElem'],
  createAllElem: createAllElem as unknown as NineGQLUIAdapter['createAllElem'],
  valueSetFilter,
  valueGetFilter,
  generateDetailUIByKey: null, // ninegql/index.ts 의 setUIAdapter 가 주입.
}

export default NineGQLUI
